import React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CreditCard, Clock, ShieldCheck, AlertCircle } from "lucide-react";

/* -------------------------------------------------------------------------
   Billing UI atoms used across vendor pages.

   * <TrialCountdownPill campaign />   — small graffiti-styled pill that
     surfaces the trial state on a campaign row / detail. Auto-hides for
     paid / waived / non-trial campaigns.

   * <ReactivateButton campaign />  — chunky yellow CTA that navigates to
     the dedicated CheckoutPage. We intentionally DON'T open Razorpay
     Checkout inline here — vendors need to see the GST-inclusive
     invoice-style breakdown before paying, per Indian compliance
     expectations.
   ------------------------------------------------------------------------- */

const daysUntil = (iso) => {
  if (!iso) return null;
  const now = new Date();
  const then = new Date(iso);
  const ms = then.getTime() - now.getTime();
  if (Number.isNaN(ms)) return null;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export const TrialCountdownPill = ({ campaign }) => {
  const s = (campaign?.status || "").toLowerCase();
  const ps = (campaign?.payment_status || "").toLowerCase();
  const isTrial = !!campaign?.is_trial;

  if (s === "paused") {
    const reason = (campaign?.pause_reason || "").toLowerCase();
    return (
      <span
        className="ag-pill ag-pill--pending inline-flex items-center gap-1"
        data-testid="campaign-paused-pill"
        title={reason || "paused"}
      >
        <AlertCircle className="h-3 w-3" />
        {reason === "insufficient_wallet_balance" ? "Wallet empty" : "Paused"}
      </span>
    );
  }
  if (s === "pending_payment") {
    return (
      <span
        className="ag-pill ag-pill--pending inline-flex items-center gap-1"
        data-testid="campaign-payment-pill"
      >
        <CreditCard className="h-3 w-3" /> Awaiting payment
      </span>
    );
  }
  if (s === "trial_ended") {
    return (
      <span
        className="ag-pill ag-pill--suspended inline-flex items-center gap-1"
        data-testid="campaign-trial-ended-pill"
      >
        <AlertCircle className="h-3 w-3" /> Trial ended
      </span>
    );
  }
  if (ps === "waived") {
    return (
      <span
        className="ag-pill ag-pill--info inline-flex items-center gap-1"
        data-testid="campaign-waived-pill"
      >
        <ShieldCheck className="h-3 w-3" /> Free (waived)
      </span>
    );
  }
  if (isTrial) {
    const days = daysUntil(campaign?.trial_expires_at);
    const cap = campaign?.trial_lead_cap ?? 300;
    const used = campaign?.leads_count ?? campaign?.trial_leads_used ?? 0;
    return (
      <span
        className="ag-pill inline-flex items-center gap-1"
        style={{
          color: "#7a5400",
          background: "var(--graffiti-yellow)",
          borderColor: "var(--ink)",
        }}
        data-testid="campaign-trial-pill"
      >
        <Clock className="h-3 w-3" />
        Trial — {days != null ? `${Math.max(0, days)}d left` : "active"}
        {typeof used === "number" ? `, ${used}/${cap}` : ""}
      </span>
    );
  }
  return null;
};

export const ReactivateButton = ({ campaign, className = "", size = "sm", onResumed }) => {
  const navigate = useNavigate();
  const sizeCls = size === "lg" ? "px-6 py-3 text-sm" : "px-4 py-2 text-xs";
  const status = (campaign?.status || "").toLowerCase();
  const pausedForWallet =
    status === "paused" && (campaign?.pause_reason || "") === "insufficient_wallet_balance";

  const goToCheckout = (e) => {
    e?.preventDefault?.();
    navigate(`/vendor/checkout/${campaign.id}`);
  };

  const tryResume = async (e) => {
    e?.preventDefault?.();
    try {
      await api.resumeCampaign(campaign.id);
      onResumed?.();
    } catch (err) {
      // Insufficient balance → send them to the wallet.
      const code = err?.response?.data?.detail?.code;
      if (code === "insufficient_wallet_balance") {
        navigate("/vendor/wallet");
      }
    }
  };

  if (pausedForWallet) {
    return (
      <button
        type="button"
        onClick={tryResume}
        className={`mkt-btn ${sizeCls} ${className}`}
        data-testid="campaign-resume-button"
      >
        <CreditCard className="h-4 w-4" />
        Resume from wallet
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={goToCheckout}
      className={`mkt-btn ${sizeCls} ${className}`}
      data-testid="campaign-reactivate-button"
    >
      <CreditCard className="h-4 w-4" />
      Review &amp; pay
    </button>
  );
};

// api is imported so consumers of this module can trigger a refresh
// via api.listCampaigns() after checkout success. Kept referenced to
// keep tree-shaking honest even when Billing.jsx is imported for
// only one of its exports.
void api;

export default { TrialCountdownPill, ReactivateButton };
